import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/hooks/useToast';
import { ErrorBanner, expiryLabel } from '../borrow/ui';
import { useInventory } from '../useInventory';
import { pickBatches } from '../transfers/batchPicking';
import { useDispenseWardRequest } from './hooks';
import { PriorityBadge } from './ui';
import type { WardRequest } from './api';

interface PickRow { inventory_id: number; batch_number: string; expiry_date: string; available: number; quantity: string }

/**
 * Supply a ward request from the IP store: batches are pre-picked earliest
 * expiry first, up to what was asked. Send less (or nothing) for a line only
 * with a reason — the ward sees it. One admission bill for the whole request,
 * settled at discharge. The server re-checks stock, drug and store.
 */
export default function DispenseRequestDialog({ request, onClose }: { request: WardRequest; onClose: () => void }) {
  const inventory = useInventory({ channel: 'ip_order' });
  const dispense = useDispenseWardRequest();
  const [picks, setPicks] = useState<Record<number, PickRow[]>>({});
  const [shortReasons, setShortReasons] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const [filled, setFilled] = useState(false);

  // The IP store's sellable shelf, in the shape pickBatches works on.
  const shelf = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return (inventory.data ?? [])
      .filter((b) => b.quantity_available > 0 && b.expiry_date.slice(0, 10) > today)
      .map((b) => ({ inventory_id: b.id, medicine_id: b.medicine_id, batch_number: b.batch_number, expiry_date: b.expiry_date.slice(0, 10), quantity_available: b.quantity_available }));
  }, [inventory.data]);

  useEffect(() => {
    if (filled || !inventory.data) return;
    const next: Record<number, PickRow[]> = {};
    for (const it of request.items) {
      if (!it.medicine_id) { next[it.id] = []; continue; }
      const { lines } = pickBatches([{ medicine_id: it.medicine_id, quantity: it.qty_requested }], shelf);
      next[it.id] = lines.map((l) => ({
        inventory_id: l.batch.inventory_id,
        batch_number: l.batch.batch_number, expiry_date: l.batch.expiry_date,
        available: l.batch.quantity_available, quantity: String(l.quantity),
      }));
    }
    setPicks(next);
    setFilled(true);
  }, [filled, inventory.data, request.items, shelf]);

  const total = (itemId: number) => (picks[itemId] ?? []).reduce((n, p) => n + (Number(p.quantity) || 0), 0);

  const submit = () => {
    setError('');
    const lines = [];
    for (const it of request.items) {
      const rows = picks[it.id] ?? [];
      for (const p of rows) {
        const q = Number(p.quantity);
        if (p.quantity !== '' && (!Number.isInteger(q) || q < 0)) return setError(`${it.drug_name}: quantities must be whole numbers.`);
        if (q > p.available) return setError(`${it.drug_name}: only ${p.available} of batch ${p.batch_number} on the shelf.`);
      }
      const sent = total(it.id);
      if (sent > it.qty_requested) return setError(`${it.drug_name}: ${sent} is more than the ${it.qty_requested} asked for.`);
      if (sent < it.qty_requested && !(shortReasons[it.id] ?? '').trim()) {
        return setError(`${it.drug_name}: ${it.qty_requested - sent} short — say why (the ward will see it).`);
      }
      lines.push({
        item_id: it.id,
        batches: rows.filter((p) => Number(p.quantity) > 0).map((p) => ({ inventory_id: p.inventory_id, quantity: Number(p.quantity) })),
        ...(sent < it.qty_requested ? { short_reason: shortReasons[it.id]!.trim() } : {}),
      });
    }
    dispense.mutate({ id: request.id, input: { lines } }, {
      onSuccess: (r) => { toast({ title: `${request.request_number} dispensed on ${r.bill.bill_number}`, variant: 'success' }); onClose(); },
      onError: (e) => setError(e instanceof Error ? e.message : 'Could not dispense'),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[760px]">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Dispense {request.request_number} <PriorityBadge priority={request.priority} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[13px]">
          <div className="text-ink-muted">
            {request.patient_name} ({request.uhid}){request.ward_name ? ` · ${request.ward_name}${request.bed_number ? ` / bed ${request.bed_number}` : ''}` : ''}
            {request.notes ? ` · “${request.notes}”` : ''}
          </div>
          {inventory.isLoading && <div className="text-ink-muted">Loading the IP store’s shelf…</div>}
          {request.items.map((it) => {
            const rows = picks[it.id] ?? [];
            const sent = total(it.id);
            return (
              <div key={it.id} className="rounded-[var(--radius)] border border-border p-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">{it.drug_name}</span>
                  <span className={`tabular-nums ${sent < it.qty_requested ? 'text-warn-ink' : 'text-ink-muted'}`}>{sent} of {it.qty_requested}</span>
                </div>
                {!rows.length ? (
                  <div className="mt-1 text-[12px] text-warn-ink">
                    {it.medicine_id ? 'None on the IP store’s shelf.' : 'This order names no catalogue medicine, so no batch can be matched.'}
                  </div>
                ) : rows.map((p, i) => (
                  <div key={p.inventory_id} className="mt-1.5 flex flex-wrap items-center gap-2 text-[12.5px]">
                    <span className="min-w-0 flex-1">Batch {p.batch_number} · expires {expiryLabel(p.expiry_date)} · {p.available} on shelf</span>
                    <Input type="number" min={0} inputMode="numeric" className="w-20" aria-label={`Quantity from batch ${p.batch_number}`}
                      value={p.quantity}
                      onChange={(e) => setPicks((x) => ({ ...x, [it.id]: rows.map((r, j) => (j === i ? { ...r, quantity: e.target.value } : r)) }))} />
                  </div>
                ))}
                {sent < it.qty_requested && (
                  <Input className="mt-2" placeholder="Why less than asked? (shown to the ward)"
                    value={shortReasons[it.id] ?? ''} onChange={(e) => setShortReasons((s) => ({ ...s, [it.id]: e.target.value }))} />
                )}
              </div>
            );
          })}
          <p className="text-[12px] text-ink-muted">Billed to the admission and settled at discharge. Supplying is not administering — the ward still charts each dose.</p>
          <ErrorBanner message={error} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={dispense.isPending} disabled={!filled}>Dispense</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
